import React, { useEffect, useState } from 'react';
import { useAppStateContext } from '../../../hooks/useAppState';
import MultiSeriesLineChart from '../../plots/MultiSeriesLineChart';
import ZoomInfo from '../../infoSections/ZoomInfo';
import FadeTransition from '../../widgetsAndUtils/FadeTransition';

// Line chart that plots Outflow EMC vs the time elapsed since the BMP was installed
export default function EMCOverTime() {
  
    const { state, bmpState } = useAppStateContext();
    const { comparisonMode } = state.wq;

  const [showZoomInfo, setShowZoomInfo] = useState(true);
  const [analyteData, setAnalyteData] = useState({});

  useEffect(() => {
    const data = state.wq.emcOverTimeData;

    console.log('emc over time data', data)

    // Check if data is a dictionary (new format)
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      setAnalyteData(data);
    } else {
      setAnalyteData({});
    }
  }, [ state.wq.emcOverTimeData, bmpState.analysistype, bmpState.bmptype, bmpState.sitename, bmpState.bmpname ])
  
  
  // Get available analytes from current data
  const availableAnalytes = Object.keys(analyteData).sort();


  // Helper function to format time since installation
  const formatTimeSince = (seconds) => {
    const totalDays = Math.abs(Math.floor(seconds / 86400));
    const years = Math.floor(totalDays / 365);
    const months = Math.floor((totalDays % 365) / 30);
    const days = totalDays % 30;
    
    const parts = [];
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    
    const result = parts.length > 0 ? parts.join(', ') : '0 days';
    return seconds < 0 ? `${result} before installation` : `${result} after installation`;
  };

  // Tooltip renderer function
  const renderTooltip = (tooltip) => {
    const data = tooltip.d;
    const timeSince = formatTimeSince(data.time_since_installation_seconds);
    
    const content = `Site: ${data.sitename || 'N/A'}
        BMP: ${data.bmpname || 'N/A'}
        Outflow EMC: ${Number(data.outflow_emc).toFixed(2)} ${data.units || ''}
        Installation Date: ${data.bmp_installation_date || 'N/A'}
        Sample Date: ${data.sampledate || 'N/A'}
        Time Since Installation: ${timeSince}
        ${data.is_comparison === 'true' || data.is_comparison === true ? '(Comparison BMP)' : ''}
    `;

    return (
      <div
        style={{
          position: 'absolute',
          left: tooltip.x,
          top: tooltip.y,
          background: 'rgba(0,0,0,0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          pointerEvents: 'none',
          fontSize: '12px',
          zIndex: 1000,
          whiteSpace: 'pre-line',
          maxWidth: '300px',
          lineHeight: '1.4'
        }}
      >
        {content}
      </div>
    );
  };

  // Function to prepare data for a specific analyte chart
  const prepareAnalyteData = (analytename) => {
    const analyteRecords = analyteData[analytename] || [];
    
    // Separate main data from comparison data
    const mainData = analyteRecords.filter(d => d.is_comparison === 'false' || d.is_comparison === false);
    const comparisonData = analyteRecords.filter(d => d.is_comparison === 'true' || d.is_comparison === true);
    
    // Combine data for chart, using bmpname as series
    let chartData = [...mainData];
    
    if (comparisonMode && comparisonData.length > 0) {
      chartData = [...chartData, ...comparisonData];
    }
    
    return { chartData, mainData, comparisonData };
  };

  return (
    <div className="space-y-4 pb-32">

      {(availableAnalytes.length > 0) && (
        <div className="space-y-4">
          <ZoomInfo
            isVisible={showZoomInfo}
            onToggle={() => setShowZoomInfo(!showZoomInfo)}
            message="Drag horizontally across the chart to zoom into specific time periods. Double-click or use the Reset Zoom button to return to the full view."
          />
          
          {/* Charts for each analyte */}
          {availableAnalytes.map(analytename => {
            const { chartData, mainData } = prepareAnalyteData(analytename);
            
            if (chartData.length === 0) return null;
            
            return (
              <FadeTransition
                key={analytename}
                in={true}
                timeout={500}
                unmountOnExit
              >
                <div className="bg-white border rounded p-4">
                  <h4 className="text-lg font-semibold mb-2 text-gray-800">
                    {analytename}
                  </h4>
                  <div className="text-sm text-gray-600 mb-4">
                    EMC Over Time {comparisonMode && chartData.some(d => d.is_comparison === 'true' || d.is_comparison === true) ? '(with comparison BMPs)' : ''}
                  </div>
                  <MultiSeriesLineChart
                    data={chartData}
                    xAccessor={d => Number(d.time_since_installation_seconds) / 86400}
                    yAccessor={d => Number(d.outflow_emc)}
                    seriesAccessor={d => d.bmpname || d.bmp}
                    lineStyleAccessor={d => d.is_comparison === 'true' || d.is_comparison === true}
                    lineWidthAccessor={d => {
                      // In comparison mode, make main BMP line thicker
                      if (comparisonMode) {
                        return (d.is_comparison === 'true' || d.is_comparison === true) ? 2 : 4;
                      }
                      return 2; // Default line width when not in comparison mode
                    }}
                    xLabel="Days since installation"
                    yLabel={`Effluent EMC${chartData[0]?.units ? ` (${chartData[0].units})` : ''}`}
                    tooltipRenderer={renderTooltip}
                    pointRadius={4}
                    showLines={false}
                  />
                </div>
              </FadeTransition>
            );
          })}
        </div>
      )}

      {!(availableAnalytes.length > 0) && (
        <div className="text-sm text-gray-600 text-center py-8">
          No EMC over time data available. Run a water quality analysis to view the charts.
        </div>
      )}
    </div>
  );
}
  